import { useCallback, useMemo } from 'react';

import { di } from 'react-magnetic-di';

import { usePublish } from '@atlaskit/rovo-triggers/main';
import { encodeSkillPromptToAdf } from '@atlassian/conversation-assistant-content-transformer';
import { useActiveAction } from '@atlassian/conversation-assistant/controllers/active-action';
import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant/types';
import {
	type ConversationAction,
	HandleActionType,
} from '@atlassian/rovo-conversation-actions/types';
import {
	type RovoExperienceSidebarAPI,
	useRovoExperienceAPIStore,
} from '@atlassian/rovo-experience-api';

export const useRovoActionTrigger = (sidebarApi: RovoExperienceSidebarAPI, product: string) => {
	di(useActiveAction, usePublish, useRovoExperienceAPIStore);

	const { setActiveAction, activeAction } = useActiveAction();
	const publish = usePublish('ai-mate');
	const {
		isMiniModalAvailable,
		toggleConversationAssistantUIMode,
		mode: currentMode,
	} = useRovoExperienceAPIStore();

	const triggerAction = useCallback(
		(action: ConversationAction, mode?: ConversationAssistantUIModes) => {
			if (action.onClick) {
				action.onClick();
				return;
			}

			const setNewActiveAction = (actionToSet: ConversationAction) => {
				switch (actionToSet.handleType) {
					case HandleActionType.Nested:
						return;
					case HandleActionType.Skill:
						if ('staticPrompt' in actionToSet) {
							publish({
								type: 'chat-new',
								product,
								source: 'rovo-action-trigger',
								openChat: false,
								data: {
									prompt: actionToSet.staticPrompt
										? encodeSkillPromptToAdf(actionToSet.staticPrompt).adf
										: '',
									dialogues: [],
								},
								openChatMode: mode ?? 'mini-modal',
							});
							return;
						}
						break;
					case HandleActionType.Chat:
						if ('staticPrompt' in actionToSet) {
							publish({
								type: 'chat-new',
								product,
								source: 'rovo-action-trigger',
								openChat: false,
								data: {
									prompt: actionToSet.staticPrompt ?? '',
									dialogues: [],
								},
								openChatMode: mode ?? 'mini-modal',
							});
							return;
						}
						break;
					case HandleActionType.Base:
						if (actionToSet.viewConfig) {
							setActiveAction(actionToSet);
						}
						break;
					default:
						break;
				}
			};

			const targetMode = calculateTargetMode(isMiniModalAvailable, mode);

			//If there's an existing active action or the target mode is different from the current mode, set to undefined first and/or set the new mode first
			if (activeAction || targetMode !== currentMode) {
				if (activeAction) {
					setActiveAction(undefined);
				}
				if (targetMode !== currentMode) {
					toggleConversationAssistantUIMode({
						targetMode,
						sidebarApi,
					});
				}
				setTimeout(() => {
					setNewActiveAction(action);
				}, 0);
				return;
			}
			setNewActiveAction(action);
		},
		[
			setActiveAction,
			activeAction,
			toggleConversationAssistantUIMode,
			isMiniModalAvailable,
			sidebarApi,
			currentMode,
			product,
			publish,
		],
	);

	return useMemo(
		() => ({
			triggerAction,
		}),
		[triggerAction],
	);
};

const calculateTargetMode = (
	isMiniModalAvailable: boolean,
	requestedMode?: ConversationAssistantUIModes,
) => {
	if (requestedMode === ConversationAssistantUIModes.MINI_MODAL && !isMiniModalAvailable) {
		return ConversationAssistantUIModes.SIDEBAR;
	}

	if (requestedMode) {
		return requestedMode;
	}

	return isMiniModalAvailable
		? ConversationAssistantUIModes.MINI_MODAL
		: ConversationAssistantUIModes.SIDEBAR;
};
